import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { X, Briefcase, Users, Shield, Award, Lock } from 'lucide-react';
import styles from './JoinDepartmentModal.module.css';
import { useAuth } from '../context/AuthContext';

interface Position {
  id: number;
  title: string;
  description: string | null;
  department: string;
  tier: number;
  rank: number;
  guide: string | null;
  requirements: string | null;
  commitmentLevel: number;
  isActive: boolean;
  maxHolders?: number | null;
  currentHolders?: number;
}

interface JoinDepartmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  preselectedDepartment?: string | null;
}

const JoinDepartmentModal: React.FC<JoinDepartmentModalProps> = ({ 
  isOpen, 
  onClose, 
  onSuccess,
  preselectedDepartment 
}) => {
  const { user } = useAuth();
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPosition, setSelectedPosition] = useState<Position | null>(null);
  const [applicationMessage, setApplicationMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [filterDepartment, setFilterDepartment] = useState<string>('all');
  const [pendingApplicationPositionIds, setPendingApplicationPositionIds] = useState<number[]>([]);
  const [allSlotsFilled, setAllSlotsFilled] = useState(false);
  const [currentPositionIds, setCurrentPositionIds] = useState<number[]>([]);

  const departments = [
    { id: 'command', name: 'Command' },
    { id: 'operations', name: 'Operations' },
    { id: 'intelligence', name: 'Intelligence' },
    { id: 'logistics', name: 'Logistics' },
    { id: 'security', name: 'Security' },
    { id: 'medical', name: 'Medical' },
    { id: 'engineering', name: 'Engineering' },
    { id: 'communications', name: 'Communications' },
  ];

  const getTierLabel = (tier: number) => {
    switch (tier) {
      case 1: return 'Entry Level';
      case 2: return 'Mid Level';
      case 3: return 'Senior Level';
      case 4: return 'C-Suite';
      default: return 'Unknown';
    }
  };

  const getCommitmentLabel = (level: number) => {
    switch (level) {
      case 1: return 'Casual';
      case 2: return 'Regular';
      case 3: return 'Dedicated';
      default: return 'Unknown';
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchPositions();
      fetchUserApplications();
      checkAvailableSlots();
      // Set initial filter if department is preselected
      if (preselectedDepartment) {
        setFilterDepartment(preselectedDepartment.toLowerCase());
      } else {
        setFilterDepartment('all');
      }
    }
  }, [isOpen, preselectedDepartment]);

  const fetchPositions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/positions', {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      console.log('Fetched positions:', response.data); // Debug log
      // Only show active positions
      const activePositions = response.data.filter((p: Position) => p.isActive);
      console.log('Active positions:', activePositions); // Debug log
      setPositions(activePositions);
    } catch (err) {
      console.error('Failed to fetch positions:', err);
      setError('Failed to load positions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserApplications = async () => {
    try {
      const response = await axios.get('/PositionApplications/my');
      // Get position IDs of pending applications
      const pendingPositionIds = response.data
        .filter((app: any) => app.status === 1) // 1 = Pending
        .map((app: any) => app.positionId);
      setPendingApplicationPositionIds(pendingPositionIds);
    } catch (err) {
      console.error('Failed to fetch user applications:', err);
      // Don't show error to user, just log it
    }
  };

  const checkAvailableSlots = async () => {
    try {
      const response = await axios.get('/PositionApplications/available-slots');
      setAllSlotsFilled(response.data.allSlotsFilled);
      setCurrentPositionIds(response.data.currentPositionIds || []);
      if (response.data.allSlotsFilled) {
        setError('You already have all three position slots filled. Please remove a position before applying for a new one.');
      }
    } catch (err) {
      console.error('Failed to check available slots:', err);
    }
  };

  const handleApply = async () => {
    if (!selectedPosition) return;

    try {
      setSubmitting(true);
      setError('');

      await axios.post('/PositionApplications', {
        positionId: selectedPosition.id,
        applicationMessage: applicationMessage.trim() || null,
      });

      alert('Application submitted successfully! A department leader will review it soon.');
      onClose();
      if (onSuccess) onSuccess();
    } catch (err: any) {
      console.error('Failed to submit application:', err);
      setError(err.response?.data || 'Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredPositions = filterDepartment === 'all'
    ? positions
    : positions.filter(p => p.department.toLowerCase() === filterDepartment.toLowerCase());

  const groupedPositions = filteredPositions.reduce((acc, position) => {
    const dept = position.department;
    if (!acc[dept]) acc[dept] = [];
    acc[dept].push(position);
    return acc;
  }, {} as Record<string, Position[]>);

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>
            <Briefcase size={24} />
            Apply for a Position
          </h2>
          <button className={styles.closeButton} onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {!selectedPosition ? (
          <div className={styles.modalBody}>
            <p className={styles.intro}>
              {preselectedDepartment 
                ? `Browse available positions in ${departments.find(d => d.id === preselectedDepartment.toLowerCase())?.name || 'this department'} or explore other departments.`
                : 'Browse available positions across all departments and apply for the one that matches your skills and interests.'
              }
            </p>

            <div className={styles.filterSection}>
              <label>Filter by Department:</label>
              <select
                value={filterDepartment}
                onChange={(e) => setFilterDepartment(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="all">All Departments</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </select>
            </div>

            {loading ? (
              <div className={styles.loading}>Loading positions...</div>
            ) : filteredPositions.length === 0 ? (
              <div className={styles.emptyState}>
                <Briefcase size={48} />
                <p>No active positions available at this time.</p>
              </div>
            ) : (
              <div className={styles.positionsList}>
                {Object.entries(groupedPositions).map(([dept, deptPositions]) => (
                  <div key={dept} className={styles.departmentGroup}>
                    <h3 className={styles.departmentName}>
                      <Users size={20} />
                      {departments.find(d => d.id === dept.toLowerCase())?.name || dept}
                    </h3>
                    {deptPositions.map(position => {
                      const hasPendingApplication = pendingApplicationPositionIds.includes(position.id);
                      const alreadyHasPosition = currentPositionIds.includes(position.id);
                      const userOrgLevel = user?.orgLevel || 0;
                      
                      // Check if position is at capacity
                      const isAtCapacity = position.maxHolders 
                        ? (position.currentHolders || 0) >= position.maxHolders 
                        : false;
                      
                      // Check tier requirements (minimum org level required, higher levels can apply)
                      let lacksRequiredRank = false;
                      let rankRequirement = '';
                      
                      // Tier 4 positions require Org Level 7+
                      if (position.tier === 4 && userOrgLevel < 7) {
                        lacksRequiredRank = true;
                        rankRequirement = `Requires Org Lvl 7+ (You: Lvl ${userOrgLevel})`;
                      } 
                      // Tier 3 positions require Org Level 4+
                      else if (position.tier === 3 && userOrgLevel < 4) {
                        lacksRequiredRank = true;
                        rankRequirement = `Requires Org Lvl 4+ (You: Lvl ${userOrgLevel})`;
                      } 
                      // Tier 2 positions require Org Level 2+
                      else if (position.tier === 2 && userOrgLevel < 2) {
                        lacksRequiredRank = true;
                        rankRequirement = `Requires Org Lvl 2+ (You: Lvl ${userOrgLevel})`;
                      }
                      // Tier 1 positions have no level requirement (all can apply)
                      
                      const isDisabled = hasPendingApplication || allSlotsFilled || alreadyHasPosition || lacksRequiredRank || isAtCapacity;
                      
                      return (
                        <div
                          key={position.id}
                          className={`${styles.positionCard} ${isDisabled ? styles.positionDisabled : ''}`}
                          onClick={() => !isDisabled && setSelectedPosition(position)}
                          style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                        >
                          <div className={styles.positionHeader}>
                            <h4>{position.title}</h4>
                            <div className={styles.badges}>
                              {isAtCapacity && (
                                <span className={`${styles.badge} ${styles.capacityBadge}`}>
                                  <Users size={12} />
                                  Full ({position.currentHolders}/{position.maxHolders})
                                </span>
                              )}
                              {lacksRequiredRank && (
                                <span className={`${styles.badge} ${styles.lockedBadge}`}>
                                  <Lock size={12} />
                                  {rankRequirement}
                                </span>
                              )}
                              {alreadyHasPosition && (
                                <span className={`${styles.badge} ${styles.currentBadge}`}>
                                  Current Position
                                </span>
                              )}
                              {hasPendingApplication && (
                                <span className={`${styles.badge} ${styles.pendingBadge}`}>
                                  Pending Application
                                </span>
                              )}
                              <span className={`${styles.badge} ${styles.tierBadge}`}>
                                <Shield size={14} />
                                {getTierLabel(position.tier)}
                              </span>
                              <span className={`${styles.badge} ${styles.commitmentBadge}`}>
                                <Award size={14} />
                                {getCommitmentLabel(position.commitmentLevel)}
                              </span>
                            </div>
                          </div>
                          {position.description && (
                            <p className={styles.positionDescription}>{position.description}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className={styles.modalBody}>
            <button
              className={styles.backButton}
              onClick={() => {
                setSelectedPosition(null);
                setApplicationMessage('');
                setError('');
              }}
            >
              ← Back to Positions
            </button>

            <div className={styles.selectedPosition}>
              <h3>{selectedPosition.title}</h3>
              <div className={styles.positionMeta}>
                <span className={styles.metaItem}>
                  <Users size={16} />
                  {departments.find(d => d.id === selectedPosition.department.toLowerCase())?.name || selectedPosition.department}
                </span>
                <span className={styles.metaItem}>
                  <Shield size={16} />
                  {getTierLabel(selectedPosition.tier)}
                </span>
                <span className={styles.metaItem}>
                  <Award size={16} />
                  {getCommitmentLabel(selectedPosition.commitmentLevel)}
                </span>
              </div>

              {selectedPosition.description && (
                <div className={styles.positionDetails}>
                  <h4>Description</h4>
                  <p>{selectedPosition.description}</p>
                </div>
              )}

              {selectedPosition.guide && (
                <div className={styles.positionDetails}>
                  <h4>Responsibilities</h4>
                  <p>{selectedPosition.guide}</p>
                </div>
              )}

              {selectedPosition.requirements && (
                <div className={styles.positionDetails}>
                  <h4>Requirements</h4>
                  <p>{selectedPosition.requirements}</p>
                </div>
              )}
            </div>

            <div className={styles.applicationForm}>
              <label htmlFor="applicationMessage">
                Why do you want this position? <span className={styles.optional}>(Optional)</span>
              </label>
              <textarea
                id="applicationMessage"
                value={applicationMessage}
                onChange={(e) => setApplicationMessage(e.target.value)}
                placeholder="Tell us why you're interested in this position and what makes you a good fit..."
                rows={4}
                className={styles.textarea}
              />
            </div>

            {error && <div className={styles.error}>{error}</div>}

            <div className={styles.modalFooter}>
              <button
                className={styles.cancelButton}
                onClick={() => {
                  setSelectedPosition(null);
                  setApplicationMessage('');
                  setError('');
                }}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                className={styles.submitButton}
                onClick={handleApply}
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JoinDepartmentModal;
